// CTM Media Post List Custom Element
class CtmMediaPostList extends HTMLElement {
  constructor() {
    super();
    this.submitBtn = this.querySelector(".ctm-submit-entry-btn");
    this.entryForm = this.querySelector(".ctm-entry-form");
    this.submitEntryContainer = this.querySelector(".ctm-submit-entry-container");
    this.closeBtn = this.querySelector(".ctm-close-btn");
    this.submitFormBtn = this.querySelector(".ctm-submit-btn");
    this.termsCheckbox = this.querySelector("#terms");
    this.postLinkInput = this.querySelector("#post-link");
    this.captionInput = this.querySelector("#caption");
    this.errorMessage = this.querySelector(".ctm-error-message");
    // Field-specific error nodes (added in section markup)
    this.postLinkErrorMessage = this.querySelector(".ctm-error-message--post-link");
    this.termsErrorMessage = this.querySelector(".ctm-error-message--terms");
    this.loadMoreContainer = this.querySelector(".ctm-media-post-list__load-more");
    this.loadMoreBtn = this.querySelector(".ctm-load-more .ctm-load-btn--more");
    this.loadLessBtn = this.querySelector(".ctm-load-more .ctm-load-btn--less");
    this.showcasePanel = this.querySelector(".ctm-showcase-panel");
    this.mediaSlider = this.querySelector(".ctm-media-slider");
    this.mediaItems = this.querySelectorAll(".ctm-media-item");
    this.mediaRecordCount = this.querySelectorAll(".ctm-media-item:not(.ctm-media-item--placeholder)").length;
    this.flickity = null;
    this.mediaSliderRetryTimer = null;
    this.mediaSliderRetryCount = 0;
    this.fallbackSlideIndex = 0;
    this.useMediaFallbackSlider = true;
    this.mediaDrag = {
      active: false,
      dragging: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      suppressClickUntil: 0
    };
    this.visibleItems = 4;
    this.pageNo = 1;
    this.pageSize = 4;
    this.cursorId = null;
    this.hasMore = true;
    this.isFetchingMoreMedia = false;
    this.lastPostLink = null; 
    this.sceneIdentity = 'FRAME_YOUR_MOMENT';
    this.shopId = this.dataset.shopId || (window.theme?.tps?.shopId) || null;
    this.customerId = this.dataset.customerId || (window.theme?.tps?.customerId) || null;
    this.hasScrolledToReturnAnchor = false;
    this.init();
  }

  connectedCallback() {
    setTimeout(async () => {
      await this.fetchMediaPageAndRender();
      this.initMediaSlider();
      this.updateMediaVisibility();
      this.scrollToReturnAnchor();
    }, 0);
  }
  
  clearErrors() {
    // Clear error state; CSS controls visibility of error messages
    [this.postLinkInput].filter(Boolean).forEach((input) => {
      if (input.parentElement) input.parentElement.classList.remove("error");
    });
    if (this.termsCheckbox?.parentElement) this.termsCheckbox.parentElement.classList.remove("error");
  }

  resetForm() {
    // Reset all user inputs + UI state for the entry form
    if (this.postLinkInput) this.postLinkInput.value = "";
    if (this.captionInput) this.captionInput.value = "";
    if (this.termsCheckbox) this.termsCheckbox.checked = false;

    const socialMediaImages = this.querySelector('.ctm-media-review');
    if (socialMediaImages) {
      socialMediaImages.innerHTML = '';
      // Keep consistent with updateMediaImage(): it shows container when images exist
      socialMediaImages.style.display = 'none';
    }

    this.lastPostLink = null;
    this.clearErrors();
  }

  init() {
    this.bindEvents();
    this.updateMediaVisibility();
    // Clear any inline display styles so CSS can control visibility (e.g. after cached DOM)
    [this.postLinkErrorMessage, this.termsErrorMessage, this.errorMessage]
      .filter(Boolean)
      .forEach((el) => el.style.removeProperty('display'));
    // Add blur event listener for post link input
    if (this.postLinkInput) {
      this.postLinkInput.addEventListener('blur', () => this.handlePostLinkBlur());
    }
    // Live validation: once user fixes a field, remove the error state immediately
    if (this.termsCheckbox) {
      this.termsCheckbox.addEventListener('change', () => {
        if (this.termsCheckbox.checked) this.termsCheckbox.parentElement?.classList.remove('error');
      });
    }
    if (this.postLinkInput) {
      this.postLinkInput.addEventListener('input', () => {
        const v = this.postLinkInput.value.trim();
        // Only clear when it becomes valid; if empty we let submit handle required state
        if (v && this.isValidSocialMediaLink(v)) this.postLinkInput.parentElement?.classList.remove('error');
      });
    }
  }
  
  scrollToAnchor(targetElement) {
    const anchor = targetElement || this.querySelector('.ctm-media-grid');
    if (anchor) {
      const headerOffset = 100;
      const elementPosition = anchor.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  }

  scrollToReturnAnchor() {
    if (this.hasScrolledToReturnAnchor || !this.id) return;

    const currentHash = window.location.hash ? window.location.hash.slice(1) : '';
    if (decodeURIComponent(currentHash) !== this.id) return;

    this.hasScrolledToReturnAnchor = true;
    requestAnimationFrame(() => this.scrollToAnchor(this));
  }

  bindEvents() {
    if (this.submitBtn) {
      this.submitBtn.addEventListener("click", () => {
        if (this.entryForm) {
          const isActive = this.entryForm.classList.toggle("active");
          if (this.submitEntryContainer) {
            if (isActive) {
              this.submitEntryContainer.classList.add("ctm-hidden");
            } else {
              this.submitEntryContainer.classList.remove("ctm-hidden");
            }
          }
        }
      });
    }
    
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => {
        if (this.entryForm) {
          this.entryForm.classList.remove("active");
        }
        if (this.submitEntryContainer) {
          this.submitEntryContainer.classList.remove("ctm-hidden");
        }
        // Clear form + error UI when closing the form
        this.resetForm();
      });
    }
    
    if (this.submitFormBtn) {
      this.submitFormBtn.addEventListener("click", (e) => {
        this.submitForm(e);
      });
    }
    
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener("click", () => {
        this.goToMediaSlide('next');
      });
    }
    
    if (this.loadLessBtn) {
      this.loadLessBtn.addEventListener("click", () => {
        this.goToMediaSlide('previous');
      });
    }

    window.addEventListener("resize", () => {
      if (this.flickity) this.flickity.resize();
      this.updateMediaVisibility();
    });

    // Delete (event delegation)
    if (this.mediaSlider) {
      this.mediaSlider.addEventListener('click', (e) => {
        if (this.mediaDrag.dragging) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        if (Date.now() < this.mediaDrag.suppressClickUntil) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const target = e.target;
        const deleteBtn = target?.closest ? target.closest('.ctm-delete-btn') : null;
        if (!deleteBtn) return;

        const item = deleteBtn.closest('.ctm-media-item');
        if (!item) return;

        const payload = {
          postUrl: item.getAttribute('data-post-url'),
          imageUrl: item.getAttribute('data-image-url'),
          title: item.getAttribute('data-title'),
          id: item.getAttribute('data-id'),
          shopifyCustomerId: this.customerId || '',
          shopId: this.shopId || '',
          isOwner: 1
          
        };

        // Basic safety: need at least one identifier
        if (!payload.postUrl && !payload.imageUrl && !payload.title && !payload.id) return;

        this.removeMyPost(payload, { itemEl: item, btnEl: deleteBtn });
      });
    }

    this.bindMediaDragEvents();
  }

  
  handlePostLinkBlur() {
    const postLink = this.postLinkInput.value.trim();
    
    if (!postLink) return;
    
    // Validate if the link is from Instagram or TikTok
    if (!this.isValidSocialMediaLink(postLink)) {
      this.postLinkInput.parentElement.classList.add("error");
      return;
    }
    
    // Clear previous errors
    this.postLinkInput.parentElement.classList.remove("error");
    
    // Check if the link is the same as the last one
    if (this.lastPostLink === postLink) {
      return; 
    }
    
    // Call API to fetch images
    this.fetchSocialMediaImages(postLink);
  }
  
  isValidSocialMediaLink(url) {
    try {
      const parsedUrl = new URL(url);
      const hostname = parsedUrl.hostname.toLowerCase();
      
      // Check if the hostname contains Instagram or TikTok
      return hostname.includes('instagram.com') || 
             hostname.includes('tiktok.com');
    } catch (e) {
      return false;
    }
  }
  
  async fetchSocialMediaImages(postLink) {
    try {
      this.showLoading();

      const response = await fetch(`${theme.tps.baseUrl}/social/findImages?url=${encodeURIComponent(postLink)}`, {
        method: 'GET',
      });
      
       const data = await response.json();
       if (data.code === 200 && data.data && ((data.data.images || []).length > 0 || data.data.title)) {
         this.updateMediaImage(data.data);
         
         if (data.data.title && this.captionInput) {
           this.captionInput.value = data.data.title;
         }
         
         this.lastPostLink = postLink;
       } else {
         console.log('No images found or API error:', data.message);
       }
       
    } catch (error) {
      console.error('Error fetching social media images:', error);
    } finally {
      this.hideLoading();
    }
  }
  
  showLoading() {
    let loadingOverlay = this.querySelector('.ctm-loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.display = 'flex';
    }
  }
  
  hideLoading() {
    const loadingOverlay = this.querySelector('.ctm-loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
    }
  }
  
  getSelectedImageUrl() {
    const selectedWrapper = this.querySelector('.ctm-social-media-image-wrapper.selected img');
    return selectedWrapper ? selectedWrapper.src : null;
  }

  async submitSocialMedia(payload) {
    let submitSucceeded = false;

    try {
      this.showLoading();
      const body = {
        postUrl: payload.postUrl,
        imageUrl: payload.imageUrl,
        title: payload.title,
        shopifyCustomerId: this.customerId || '',
        shopId: this.shopId || '',
        sceneIdentity: this.sceneIdentity,
        isOwner: 1
      };
      const response = await fetch(`${theme.tps.baseUrl}/social/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (this.isSuccessResponse(data, response)) {
        submitSucceeded = true;
        CtmMessageMediaTip(data?.message || 'Submit Success');
        this.resetForm();
        await this.fetchMediaPageAndRender();
      } else {
        CtmMessageMediaTip(data?.message || 'Submit failed');
      }
    } catch (error) {
      console.error('Error submitting social media form:', error);
      if (!submitSucceeded) {
        CtmMessageMediaTip('Submit failed, please try again.');
      }
    } finally {
      this.hideLoading();
    }
  }

  async fetchMediaPageAndRender(append = false, options = {}) {
    const preserveExistingOnEmpty = Boolean(options.preserveExistingOnEmpty);
    const mergeExistingRecords = Boolean(options.mergeExistingRecords);

    try {
      this.showLoading();

      if (!append) {
        this.cursorId = null;
      }

      const params = new URLSearchParams({
        mode: 2,
        pageSize: this.pageSize,
        formType: 1,
        sceneIdentity: this.sceneIdentity
      });

      if (append && this.cursorId) {
        params.append('cursorId', this.cursorId);
      }

      if (this.customerId) params.append('shopifyCustomerId', this.customerId);
      if (this.shopId) params.append('shopId', this.shopId);
      const response = await fetch(`${theme.tps.baseUrl}/social/mediaPage?${params.toString()}`, {
        method: 'GET'
      });
      const data = await response.json();
      if (this.isSuccessResponse(data) && data.data && Array.isArray(data.data.records)) {
        let records = data.data.records;
        if (!append && mergeExistingRecords) {
          records = this.mergeMediaRecords(this.getCurrentMediaRecords(), records);
        }
        if (records.length < this.pageSize) {
          this.hasMore = false;
        } else {
          this.hasMore = true;
        }

        if (records.length > 0) {
          this.cursorId = records[records.length - 1].id;
        }

        this.toggleShowcasePanel(true);
        try {
          this.renderMediaGrid(records, append, preserveExistingOnEmpty);
        } catch (renderError) {
          console.error('Error rendering media page records:', renderError);
        }
      } else {
        console.warn('No media records returned');
        this.hasMore = false;
        this.toggleShowcasePanel(true);
        this.renderMediaGrid([], append, preserveExistingOnEmpty);
      }
    } catch (error) {
      console.error('Error fetching media page:', error);
      this.hasMore = false;
      this.toggleShowcasePanel(true);
      this.renderMediaGrid([], append, preserveExistingOnEmpty);
    } finally {
      this.hideLoading();
    }
  }

  async removeMyPost(payload, { itemEl, btnEl } = {}) {
    const confirmed = await window.tpsConfirmModal.show({
      title: 'Delete Post',
      message: 'Are you sure you want to delete this post?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (!confirmed) return;
    let deleteSucceeded = false;

    try {
      this.showLoading();
      if (btnEl) btnEl.disabled = true;

      let payloadId = this.isLocalRecordId(payload.id) ? '' : payload.id;
      if (!payloadId) {
        await this.fetchMediaPageAndRender(false, {
          preserveExistingOnEmpty: true,
          mergeExistingRecords: true
        });
        payloadId = this.findCurrentServerRecordId(payload);
      }

      const body = {
        postUrl: payload.postUrl || '',
        imageUrl: payload.imageUrl || '',
        title: payload.title || '',
        shopifyCustomerId: payload.shopifyCustomerId || this.customerId || '',
        shopId: payload.shopId || this.shopId || '',
        sceneIdentity: this.sceneIdentity,
        isOwner: Number(payload.isOwner) || 0,
        id: payloadId || ''
      };

      const response = await fetch(`${theme.tps.baseUrl}/social/removeMyPost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (this.isSuccessResponse(data, response)) {
        deleteSucceeded = true;
        CtmMessageMediaTip(data?.message || 'Delete Success');
        try {
          this.removeDeletedItemFromGrid(itemEl, body);
          await this.fetchMediaPageAndRender(false, { preserveExistingOnEmpty: true });
        } catch (renderError) {
          console.error('Error updating media grid after delete:', renderError);
        }
      } else {
        CtmMessageMediaTip(data?.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting social post:', error);
      if (!deleteSucceeded) {
        CtmMessageMediaTip('Delete failed, please try again.');
      }
    } finally {
      if (btnEl) btnEl.disabled = false;
      this.hideLoading();
    }
  }

  isSuccessResponse(data, response) {
    const code = Number(data?.code);
    const message = String(data?.message || '').trim().toLowerCase();
    return code === 0 || code === 200 || message === 'success' || (response?.ok && message === 'success');
  }

  getCurrentMediaRecords() {
    return Array.from(this.querySelectorAll('.ctm-media-item:not(.ctm-media-item--placeholder)')).map((item) => ({
      postUrl: item.getAttribute('data-post-url') || '',
      imageUrl: item.getAttribute('data-image-url') || '',
      title: item.getAttribute('data-title') || '',
      id: item.getAttribute('data-id') || '',
      isOwner: item.querySelector('.ctm-delete-btn') ? 1 : 0
    }));
  }

  isLocalRecordId(id) {
    return String(id || '').startsWith('local-');
  }

  findCurrentServerRecordId(record) {
    const targetPostUrl = record?.postUrl || '';
    const targetImageUrl = record?.imageUrl || '';
    const targetTitle = record?.title || '';

    const matchedRecord = this.getCurrentMediaRecords().find((item) => {
      if (!item.id || this.isLocalRecordId(item.id)) return false;
      if (targetPostUrl && item.postUrl === targetPostUrl) return true;
      if (targetImageUrl && item.imageUrl === targetImageUrl) return true;
      return targetTitle && item.title === targetTitle;
    });

    return matchedRecord?.id || '';
  }

  prependMediaRecord(record) {
    const existingRecords = this.getCurrentMediaRecords();
    const records = this.mergeMediaRecords([record], existingRecords);

    this.renderMediaGrid(records);
  }

  mergeMediaRecords(...recordGroups) {
    const recordsByKey = new Map();

    recordGroups.flat().forEach((item, index) => {
      const key = item.imageUrl || item.postUrl || item.id || `${item.title}-${index}`;
      const existing = recordsByKey.get(key);

      if (!existing) {
        recordsByKey.set(key, item);
        return;
      }

      const existingHasServerId = existing.id && !this.isLocalRecordId(existing.id);
      const itemHasServerId = item.id && !this.isLocalRecordId(item.id);
      if (!existingHasServerId && itemHasServerId) {
        recordsByKey.set(key, item);
      }
    });

    return Array.from(recordsByKey.values());
  }

  renderMediaGrid(records, append = false, preserveExistingOnEmpty = false) {
    const slider = this.mediaSlider || this.querySelector('.ctm-media-slider');
    if (!slider) return;

    if (!records.length) {
      if (preserveExistingOnEmpty && this.mediaRecordCount > 0) {
        this.mediaItems = this.querySelectorAll(".ctm-media-item");
        this.updateMediaVisibility();
        return;
      }
      if (!append) {
        slider.innerHTML = '';
        this.mediaRecordCount = 0;
        this.renderPlaceholderGrid(slider);
      }
      this.mediaItems = this.querySelectorAll(".ctm-media-item");
      this.initMediaSlider();
      this.updateMediaVisibility();
      return;
    }

    if (!append) {
      slider.innerHTML = '';
      this.fallbackSlideIndex = 0;
    }

    this.mediaRecordCount = append ? this.mediaRecordCount + records.length : records.length;

    // Icon templates rendered by Liquid (so JS-rendered items can reuse theme SVGs)
    const deleteIconTpl = this.querySelector('template.ctm-icon-template[data-icon="delete"]');
    let currentGrid = null;

    records.forEach((record, index) => {
      if (index % this.visibleItems === 0) {
        const slide = document.createElement('div');
        slide.classList.add('ctm-media-slide');
        currentGrid = document.createElement('div');
        currentGrid.classList.add('ctm-media-grid');
        slide.appendChild(currentGrid);
        slider.appendChild(slide);
      }

      const item = document.createElement('div');
      item.classList.add('ctm-media-item');
      // Persist record fields needed for delete API
      if (record?.postUrl) item.setAttribute('data-post-url', record.postUrl);
      if (record?.imageUrl) item.setAttribute('data-image-url', record.imageUrl);
      if (record?.title) item.setAttribute('data-title', record.title);
      if (record?.id) item.setAttribute('data-id', record.id)

      const imageWrapper = document.createElement('div');
      imageWrapper.classList.add('ctm-media-image');

      if (record.imageUrl) {
        const img = document.createElement('img');
        img.alt = 'Post showcase media';
        img.classList.add('ctm-media-img');
        img.addEventListener('error', () => {
          img.remove();
          if (!imageWrapper.querySelector('.ctm-media-placeholder')) {
            imageWrapper.appendChild(this.createImagePlaceholder());
          }
        }, { once: true });
        img.src = record.imageUrl;
        imageWrapper.appendChild(img);
      } else {
        imageWrapper.appendChild(this.createImagePlaceholder());
      }

      const actions = document.createElement('div');
      actions.classList.add('ctm-media-actions');
      const actionsCover = document.createElement('div');
      actionsCover.classList.add('ctm-media-actions-cover');
      const visitLink = document.createElement('a');
      visitLink.href = record.postUrl || '#';
      visitLink.target = '_blank';
      visitLink.rel = 'noopener';
      visitLink.classList.add('ctm-visit-link');
      visitLink.textContent = 'visit link';
      actionsCover.appendChild(visitLink);

      // Delete button: only show when backend marks the record as owner (isOwner == 1)
      // Note: isOwner may come as number (1) or string ("1")
      if (this.customerId && Number(record?.isOwner) === 1) {
        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.classList.add('ctm-delete-btn');
        if (deleteIconTpl?.content) {
          deleteBtn.appendChild(deleteIconTpl.content.cloneNode(true));
        } else {
          // Fallback: text label so the button doesn't disappear if template is missing
          deleteBtn.textContent = 'delete';
        }
        actionsCover.appendChild(deleteBtn);
      }

      actions.appendChild(actionsCover);
      imageWrapper.appendChild(actions);
     
      item.appendChild(imageWrapper);

      currentGrid.appendChild(item);
    });

    this.mediaItems = this.querySelectorAll(".ctm-media-item");
    this.initMediaSlider();
    this.updateMediaVisibility();
  }

  removeDeletedItemFromGrid(itemEl, record = {}) {
    const slider = this.mediaSlider || this.querySelector('.ctm-media-slider');
    if (!itemEl || !itemEl.isConnected) {
      itemEl = this.findMediaItemElement(record);
    }
    if (!itemEl) return;

    itemEl.remove();

    this.querySelectorAll('.ctm-media-grid').forEach((grid) => {
      if (!grid.querySelector('.ctm-media-item')) {
        grid.closest('.ctm-media-slide')?.remove();
      }
    });

    this.mediaItems = this.querySelectorAll(".ctm-media-item");
    this.mediaRecordCount = this.querySelectorAll(".ctm-media-item:not(.ctm-media-item--placeholder)").length;

    if (this.mediaRecordCount === 0 && slider) {
      slider.innerHTML = '';
      this.renderPlaceholderGrid(slider);
      this.mediaItems = this.querySelectorAll(".ctm-media-item");
    }

    this.initMediaSlider();
    this.updateMediaVisibility();
  }

  async goToMediaSlide(direction) {
    if (this.flickity && !this.useMediaFallbackSlider) {
      if (direction === 'next') {
        this.flickity.next();
      } else {
        this.flickity.previous();
      }
      return;
    }

    const slidesCount = this.querySelectorAll('.ctm-media-slide').length;
    if (slidesCount <= 1 && !(direction === 'next' && this.hasMore)) return;

    if (direction === 'next') {
      if (this.fallbackSlideIndex >= slidesCount - 1) {
        await this.loadNextMediaPage();
        const nextSlidesCount = this.querySelectorAll('.ctm-media-slide').length;
        if (nextSlidesCount > slidesCount) {
          this.fallbackSlideIndex = Math.min(this.fallbackSlideIndex + 1, nextSlidesCount - 1);
        }
      } else {
        this.fallbackSlideIndex = Math.min(this.fallbackSlideIndex + 1, slidesCount - 1);
      }
    } else {
      this.fallbackSlideIndex = Math.max(this.fallbackSlideIndex - 1, 0);
    }

    this.updateMediaVisibility();
  }

  async loadNextMediaPage() {
    if (!this.hasMore || this.isFetchingMoreMedia) return;

    this.isFetchingMoreMedia = true;
    if (this.loadMoreBtn) this.loadMoreBtn.classList.add('loading');

    try {
      await this.fetchMediaPageAndRender(true, { preserveExistingOnEmpty: true });
    } finally {
      this.isFetchingMoreMedia = false;
      if (this.loadMoreBtn) this.loadMoreBtn.classList.remove('loading');
      this.updateMediaVisibility();
    }
  }

  bindMediaDragEvents() {
    const slider = this.mediaSlider || this.querySelector('.ctm-media-slider');
    if (!slider || !window.PointerEvent) return;

    const getSlidesCount = () => this.querySelectorAll('.ctm-media-slide').length;
    const canDrag = () => this.mediaRecordCount > this.visibleItems && getSlidesCount() > 1;
    const isInteractiveTarget = (target) => target?.closest?.('a, button, input, textarea, select, label');

    const setDragOffset = (offset) => {
      slider.style.setProperty('--ctm-drag-offset', `${offset}px`);
    };

    const stopDrag = (event) => {
      if (!this.mediaDrag.active) return;

      const width = slider.getBoundingClientRect().width || 1;
      const deltaX = this.mediaDrag.currentX - this.mediaDrag.startX;
      const threshold = Math.min(90, Math.max(40, width * 0.16));
      const slidesCount = getSlidesCount();
      const selectedIndex = Math.min(this.fallbackSlideIndex, Math.max(slidesCount - 1, 0));
      const direction = deltaX < 0 ? 'next' : 'previous';
      const canChange = direction === 'next' ? selectedIndex < slidesCount - 1 : selectedIndex > 0;
      const shouldChange = this.mediaDrag.dragging && canChange && Math.abs(deltaX) > threshold;

      this.mediaDrag.active = false;
      if (this.mediaDrag.dragging) {
        this.mediaDrag.suppressClickUntil = Date.now() + 250;
      }

      if (shouldChange) {
        const nextIndex = direction === 'next' ? selectedIndex + 1 : selectedIndex - 1;
        const releaseOffset = direction === 'next' ? deltaX + width : deltaX - width;
        this.fallbackSlideIndex = nextIndex;
        this.updateMediaVisibility();
        setDragOffset(releaseOffset);
        window.requestAnimationFrame(() => {
          slider.classList.remove('is-dragging');
          setDragOffset(0);
        });
      } else {
        slider.classList.remove('is-dragging');
        setDragOffset(0);
        this.updateMediaVisibility();
      }

      if (event?.pointerId !== undefined && slider.releasePointerCapture) {
        try {
          slider.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture may already be released by the browser.
        }
      }

      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointercancel', stopDrag);

      window.setTimeout(() => {
        this.mediaDrag.dragging = false;
      }, 0);
    };

    const onPointerMove = (event) => {
      if (!this.mediaDrag.active || event.pointerId !== this.mediaDrag.pointerId) return;

      const deltaX = event.clientX - this.mediaDrag.startX;
      const deltaY = event.clientY - this.mediaDrag.startY;

      if (!this.mediaDrag.dragging) {
        if (Math.abs(deltaY) > 8 && Math.abs(deltaY) > Math.abs(deltaX)) {
          stopDrag(event);
          return;
        }
        if (Math.abs(deltaX) < 8) return;
        this.mediaDrag.dragging = true;
        slider.classList.add('is-dragging');
      }

      event.preventDefault();
      this.mediaDrag.currentX = event.clientX;

      const slidesCount = getSlidesCount();
      const selectedIndex = Math.min(this.fallbackSlideIndex, Math.max(slidesCount - 1, 0));
      const atStart = selectedIndex === 0 && deltaX > 0;
      const atEnd = selectedIndex === slidesCount - 1 && deltaX < 0;
      const safeDeltaX = atStart || atEnd ? deltaX * 0.28 : deltaX;

      setDragOffset(safeDeltaX);
    };

    slider.addEventListener('pointerdown', (event) => {
      if (event.button && event.button !== 0) return;
      if (!canDrag() || isInteractiveTarget(event.target)) return;

      this.mediaDrag.active = true;
      this.mediaDrag.dragging = false;
      this.mediaDrag.pointerId = event.pointerId;
      this.mediaDrag.startX = event.clientX;
      this.mediaDrag.startY = event.clientY;
      this.mediaDrag.currentX = event.clientX;

      if (slider.setPointerCapture) {
        slider.setPointerCapture(event.pointerId);
      }

      window.addEventListener('pointermove', onPointerMove, { passive: false });
      window.addEventListener('pointerup', stopDrag);
      window.addEventListener('pointercancel', stopDrag);
    });
  }

  findMediaItemElement(record = {}) {
    const id = record.id || '';
    const postUrl = record.postUrl || '';
    const imageUrl = record.imageUrl || '';
    const title = record.title || '';

    return Array.from(this.querySelectorAll('.ctm-media-item:not(.ctm-media-item--placeholder)')).find((item) => {
      if (id && item.getAttribute('data-id') === id) return true;
      if (postUrl && item.getAttribute('data-post-url') === postUrl) return true;
      if (imageUrl && item.getAttribute('data-image-url') === imageUrl) return true;
      return title && item.getAttribute('data-title') === title;
    }) || null;
  }
  
  updateMediaImage(data) {
        const socialMediaImages = this.querySelector('.ctm-media-review');
        const images = Array.isArray(data.images) ? data.images : [];
        if (images.length === 0) {
          images.unshift('https://cdn.shopify.com/s/files/1/0243/8735/4704/files/empty-image.jpg?v=1766479764');
        }
        if (images.length > 0) {
          socialMediaImages.innerHTML = '';
          images.forEach((image, index) => {
            const imageWrapper = document.createElement('div');
            imageWrapper.classList.add('ctm-social-media-image-wrapper');
            imageWrapper.dataset.imageIndex = index;
            
            if (index === 0) {
              imageWrapper.classList.add('selected');
            }
            
            const img = document.createElement('img');
            img.src = image;
            img.classList.add('ctm-social-media-image');
            img.alt = 'Social Media Post';
            
            const checkmark = document.createElement('span');
            checkmark.classList.add('ctm-image-checkmark');
            checkmark.innerHTML = '✓';
            
            imageWrapper.appendChild(img);
            imageWrapper.appendChild(checkmark);
            
            imageWrapper.addEventListener('click', () => {
              const allWrappers = socialMediaImages.querySelectorAll('.ctm-social-media-image-wrapper');
              allWrappers.forEach(wrapper => {
                wrapper.classList.remove('selected');
              });
              
              imageWrapper.classList.add('selected');
            });
            
            socialMediaImages.appendChild(imageWrapper);
          });
          socialMediaImages.style.display = 'flex';
        }
  }
  async submitForm(e) {
    e.preventDefault();
    // Clear previous errors (CSS shows messages when parent has .error)
    this.clearErrors();
    
    const postLink = this.postLinkInput.value.trim();
    const caption = this.captionInput?.value.trim() || postLink;
    // const mediaFiles = this.mediaInput.files;
    const termsAccepted = this.termsCheckbox.checked;
    const selectedImageUrl = this.getSelectedImageUrl();
    
    let isValid = true;
    
    if (!postLink || !this.isValidSocialMediaLink(postLink)) {
      this.postLinkInput.parentElement.classList.add("error");
      isValid = false;
    }
    
    if (!termsAccepted) {
      this.termsCheckbox.parentElement.classList.add("error");
      isValid = false;
    }
    
    if (!selectedImageUrl) {
      isValid = false;
    }
    
    if (isValid) {
      await this.submitSocialMedia({
        postUrl: postLink,
        imageUrl: selectedImageUrl,
        title: caption
      });
    }
  }
  
  isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  }

  createImagePlaceholder() {
    const placeholder = document.createElement('div');
    placeholder.classList.add('ctm-media-placeholder');
    placeholder.setAttribute('aria-hidden', 'true');
    return placeholder;
  }

  renderPlaceholderGrid(slider, count = this.visibleItems) {
    const slide = document.createElement('div');
    slide.classList.add('ctm-media-slide', 'ctm-media-slide--placeholder');

    const grid = document.createElement('div');
    grid.classList.add('ctm-media-grid');

    for (let i = 0; i < count; i += 1) {
      const item = document.createElement('div');
      item.classList.add('ctm-media-item', 'ctm-media-item--placeholder');

      const imageWrapper = document.createElement('div');
      imageWrapper.classList.add('ctm-media-image', 'tps-skeleton');
      imageWrapper.appendChild(this.createImagePlaceholder());

      item.appendChild(imageWrapper);
      grid.appendChild(item);
    }

    slide.appendChild(grid);
    slider.appendChild(slide);
  }

  toggleShowcasePanel(isVisible) {
    if (!this.showcasePanel) return;
    this.showcasePanel.hidden = !isVisible;
  }
  
  initMediaSlider() {
    const slider = this.mediaSlider || this.querySelector('.ctm-media-slider');
    if (!slider) return;
    if (this.useMediaFallbackSlider) {
      if (this.flickity) {
        this.flickity.destroy();
        this.flickity = null;
      }
      slider.classList.add('ctm-media-slider--fallback');
      this.updateMediaVisibility();
      return;
    }

    if (typeof Flickity === 'undefined') {
      slider.classList.add('ctm-media-slider--fallback');
      if (!this.mediaSliderRetryTimer && this.mediaSliderRetryCount < 20) {
        this.mediaSliderRetryCount += 1;
        this.mediaSliderRetryTimer = window.setTimeout(() => {
          this.mediaSliderRetryTimer = null;
          this.initMediaSlider();
        }, 100);
      }
      this.updateMediaVisibility();
      return;
    }

    this.mediaSliderRetryCount = 0;
    if (this.mediaSliderRetryTimer) {
      window.clearTimeout(this.mediaSliderRetryTimer);
      this.mediaSliderRetryTimer = null;
    }

    slider.classList.remove('ctm-media-slider--fallback');
    this.querySelectorAll('.ctm-media-slide').forEach((slide) => {
      slide.hidden = false;
    });

    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
    }

    try {
      this.flickity = new Flickity(slider, {
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        adaptiveHeight: false,
        draggable: true,
        groupCells: false
      });
    } catch (error) {
      console.error('Error initializing CTM media slider:', error);
      this.flickity = null;
      slider.classList.add('ctm-media-slider--fallback');
      this.updateMediaVisibility();
      return;
    }

    this.flickity.on('change', () => this.updateMediaVisibility());
    this.flickity.on('settle', () => this.updateMediaVisibility());
    this.updateMediaVisibility();
  }

  updateMediaVisibility() {
    const slideEls = Array.from(this.querySelectorAll('.ctm-media-slide'));
    const isFallbackSlider = this.useMediaFallbackSlider || !this.flickity;
    const slidesCount = isFallbackSlider ? slideEls.length : this.flickity.slides.length;
    if (isFallbackSlider) {
      this.fallbackSlideIndex = Math.min(this.fallbackSlideIndex, Math.max(slidesCount - 1, 0));
    }
    const selectedIndex = isFallbackSlider ? this.fallbackSlideIndex : this.flickity.selectedIndex;
    const canLoadMore = this.hasMore && this.mediaRecordCount >= this.visibleItems;
    const canScroll = this.mediaRecordCount > this.visibleItems && slidesCount > 1;
    const canScrollPrev = canScroll && selectedIndex > 0;
    const canAdvanceExistingSlides = canScroll && selectedIndex < slidesCount - 1;
    const canScrollNext = canAdvanceExistingSlides || canLoadMore;
    const shouldShowControls = canScroll || canLoadMore;
    const slider = this.mediaSlider || this.querySelector('.ctm-media-slider');

    if (slider) {
      slider.classList.toggle('ctm-media-slider--fallback', isFallbackSlider);
      slider.classList.toggle('is-draggable', isFallbackSlider && canScroll);
      slider.style.setProperty('--ctm-slider-index', String(selectedIndex));
    }

    slideEls.forEach((slide) => {
      slide.hidden = false;
    });

    if (this.loadMoreContainer) {
      this.loadMoreContainer.hidden = !shouldShowControls;
    }

    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = shouldShowControls ? "inline-flex" : "none";
      this.loadMoreBtn.disabled = !canScrollNext || this.isFetchingMoreMedia;
      this.loadMoreBtn.classList.toggle("is-disabled", !canScrollNext || this.isFetchingMoreMedia);
    }
    
    if (this.loadLessBtn) {
      this.loadLessBtn.style.display = shouldShowControls ? "inline-flex" : "none";
      this.loadLessBtn.disabled = !canScrollPrev;
      this.loadLessBtn.classList.toggle("is-disabled", !canScrollPrev);
    }
    
    if (this.mediaItems) {
      for (let i = 0; i < this.mediaItems.length; i++) {
        this.mediaItems[i].style.display = "block";
      }
    }
  }
}

customElements.define("ctm-media-post-list", CtmMediaPostList);

function CtmMessageMediaTip(message) {
  let tip = document.querySelector('.ctm-message-media-tip');
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'ctm-message-media-tip';
    document.body.appendChild(tip);
  }
  tip.textContent = message;
  // Force reflow
  tip.offsetHeight; 
  tip.classList.add('show');
  
  if (tip.timeoutId) {
    clearTimeout(tip.timeoutId);
  }
  
  tip.timeoutId = setTimeout(() => {
    tip.classList.remove('show');
  }, 3000);
}
